import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type HallOfFameEntry = {
  student_id: string;
  avatar_url: string | null;
  student_avatar_image_temporary: string | null;
  points: number;
  level: number;
  is_current_user: boolean;
  rank: number;
};

export type PreviousChampion = {
  student_id: string;
  avatar_url: string | null;
  student_avatar_image_temporary: string | null;
  points: number;
  placement: number;
};

export const useHallOfFame = () => {
  const {
    data: entries,
    isLoading: isLoadingEntries,
    error: entriesError,
    refetch,
  } = useQuery<HallOfFameEntry[]>({
    queryKey: ["hall-of-fame"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_hall_of_fame" as never);

      if (error) {
        console.error("[useHallOfFame] RPC error:", error.message);
        throw error;
      }

      return (data ?? []) as HallOfFameEntry[];
    },
    staleTime: 60_000,
  });

  const { data: previousChampions, isLoading: isLoadingChampions } = useQuery<PreviousChampion[]>({
    queryKey: ["hall-of-fame-previous-champions"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_last_week_champions" as never);

      if (error) {
        console.error("[useHallOfFame] previous champions RPC error:", error.message);
        throw error;
      }

      return (data ?? []) as PreviousChampion[];
    },
    staleTime: 60_000,
  });

  const allEntries = entries ?? [];
  const top10 = allEntries.filter((e) => e.rank <= 10);
  const currentUserOutside = allEntries.find((e) => e.is_current_user && e.rank > 10);

  const currentUserEntry = allEntries.find((e) => e.is_current_user);
  const currentUserRank = currentUserEntry?.rank ?? null;

  return {
    entries: top10,
    currentUserRow: currentUserOutside ?? null,
    currentUserRank,
    previousChampions: previousChampions ?? [],
    isLoading: isLoadingEntries || isLoadingChampions,
    error: entriesError,
    refetch,
  };
};
